import { useEffect, useState, useMemo, useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';
import { ISftpUserData } from 'sections/user-management/entities';
import * as RestApi from 'core/network/RestApi';
import { useHistory, useParams } from 'react-router-dom';
import {
  MainArea,
  TopArea,
  Page,
  SingleColumnLayout,
  Grid,
  Form,
  Input,
  Panel,
  SelectMulti,
  Button,
  BottomBar,
  SaveBar,
  P,
  Gap,
  ISelectMultiOption,
  RadioButton,
  Box,
} from '@salecycle/ui-component-library';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import { useTranslation } from 'react-i18next';
import Spinner from 'core/ui/components/spinner';
import { Brand, Group, Site } from 'sections/client-management/entities';
import { useClients } from 'core/app/AppContext';
import { entityToSortedSelectOptions } from 'components/utils/getSelectOptions';
import { SftpPasswordResetModal } from 'sections/user-management/modals/SftpPasswordResetModal';
import { UnsavedChangesModal } from 'core/modals/UnsavedChangesModal';
import { updateSftpPermissions } from '../queries/queries';
import useNotifications from 'core/notifications/useNotifications';

export function EditSftpUserPage() {
  const { push } = useHistory();
  const { t } = useTranslation(['common', 'sftpUserManagement']);
  const { username } = useParams<{ username: string }>();
  const { showNotification } = useNotifications();
  const { getSites, getBrands, getGroups } = useClients();
  const { mutate } = useMutation(updateSftpPermissions);
  const [email, setEmail] = useState('');
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [brands, setBrands] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userType, setUserType] = useState('');
  const [unsavedChangesModalIsOpen, setUnsavedChangesModalIsOpen] = useState(false);
  const [saveEnabled, setSaveEnabled] = useState(false);

  const siteOptions = useMemo(() => entityToSortedSelectOptions<Site>(getSites()), [getSites]);
  const brandOptions = useMemo(() => entityToSortedSelectOptions<Brand>(getBrands()), [getBrands]);
  const groupOptions = useMemo(() => entityToSortedSelectOptions<Group>(getGroups()), [getGroups]);

  const sftpUserQuery = useQuery(['get-sftp-user'], async () => {
    const result = await RestApi.get<ISftpUserData>(`/public_sftp_users/${username}`);
    return result.data;
  });

  const sftpUser: ISftpUserData = sftpUserQuery?.data ?? null;

  const getSiteMappingsFromSites = useCallback(() => sites?.map(s => s.value).join(','), [sites]);
  const getBrandMappingsFromBrands = useCallback(() => brands?.map(b => b.value).join(','), [brands]);
  const getGroupMappingsFromGroups = useCallback(() => groups?.map(g => g.value).join(','), [groups]);

  const handleResetPasswordButtonClicked = () => {
    setPasswordResetModalOpen(true);
  };

  const handleUserTypeChange = (userType: string) => {
    setUserType(userType);
  };

  const gotoSftpUserManagementPage = () => {
    push('/user-management/sftp-users');
  };

  const discardChanges = () => {
    setSites(getClientOptionsFromMappings('sites')(sftpUser.sites));
    setBrands(getClientOptionsFromMappings('brands')(sftpUser.brands));
    setGroups(getClientOptionsFromMappings('groups')(sftpUser.groups));
    setUserType(sftpUser.userType);
  };

  const getClientSelectOptions = useCallback(
    (type: string): ISelectMultiOption<string>[] => {
      switch (type) {
        case 'sites':
          return siteOptions;
        case 'brands':
          return brandOptions;
        case 'groups':
          return groupOptions;
      }
    },
    [siteOptions, brandOptions, groupOptions],
  );

  const getValue = (type: string): ISelectMultiOption<string>[] => {
    switch (type) {
      case 'sites':
        return sites;
      case 'brands':
        return brands;
      case 'groups':
        return groups;
    }
  };

  const getClientOptionsFromMappings = useCallback(
    (type: string) => {
      const clientOptions = getClientSelectOptions(type);
      return (clientMappings: string) => {
        if (!clientMappings) {
          return [];
        }

        return clientMappings.split(',').map(groupId => {
          return clientOptions.find(o => o.value === groupId);
        });
      };
    },
    [getClientSelectOptions],
  );

  const onSave = () => {
    setSaveEnabled(false);
    const updatedUser = {
      ...sftpUser,
      sites: getSiteMappingsFromSites(),
      brands: getBrandMappingsFromBrands(),
      groups: getGroupMappingsFromGroups(),
      userType,
    };
    mutate(updatedUser, {
      onSuccess: () => {
        showNotification(`${t('sftpUserManagement:editSftpUser.save.notification.success', { username })}`);
        gotoSftpUserManagementPage();
      },
      onError: () => {
        showNotification(t('sftpUserManagement:editSftpUser.save.notification.error'), 'error');
        setSaveEnabled(true);
      },
    });
  };

  const getClientChangeHandler = (type: string) => {
    const setters = {
      sites: setSites,
      brands: setBrands,
      groups: setGroups,
    };
    return (selectedOptions: ISelectMultiOption<string>[]) => {
      setters[type](selectedOptions);
    };
  };

  useEffect(() => {
    if (!sftpUserQuery.isLoading) {
      setEmail(sftpUser.email);
      setSites(getClientOptionsFromMappings('sites')(sftpUser.sites));
      setGroups(getClientOptionsFromMappings('groups')(sftpUser.groups));
      setBrands(getClientOptionsFromMappings('brands')(sftpUser.brands));
      setUserType(sftpUser.userType);
    }
  }, [sftpUserQuery.isLoading, sftpUser, getClientOptionsFromMappings]);

  useEffect(
    () =>
      setSaveEnabled(
        sftpUser?.groups !== getGroupMappingsFromGroups() ||
          sftpUser?.brands !== getBrandMappingsFromBrands() ||
          sftpUser?.sites !== getSiteMappingsFromSites() ||
          sftpUser?.userType !== userType,
      ),
    [
      sftpUser?.sites,
      sftpUser?.brands,
      sftpUser?.groups,
      sftpUser?.userType,
      userType,
      groups,
      brands,
      sites,
      getSiteMappingsFromSites,
      getBrandMappingsFromBrands,
      getGroupMappingsFromGroups,
    ],
  );

  if (sftpUserQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <MainArea>
      <TopArea>
        <AppTopBar />
      </TopArea>
      <Page padding={0}>
        <>
          <SftpPasswordResetModal
            currentEmail={email}
            setCurrentEmail={setEmail}
            passwordResetModalOpen={passwordResetModalOpen}
            setPasswordResetModalOpen={setPasswordResetModalOpen}
          />
          <UnsavedChangesModal
            heading={t('common:common.unsavedChangesModal.heading')}
            isOpen={unsavedChangesModalIsOpen}
            onOkButtonClicked={gotoSftpUserManagementPage}
            onCancelButtonClicked={() => setUnsavedChangesModalIsOpen(false)}
          >
            <P>{t('common:editUser.unsavedChangesModal.text1')}</P>
            <Gap top={10} />
            <P>{t('common:editUser.unsavedChangesModal.text2')}</P>
          </UnsavedChangesModal>
          <SingleColumnLayout paddingLeft={20} heading={sftpUser?.username}>
            <Grid>
              <Panel padding={0} variation="clean">
                <Form>
                  <Input
                    disabled={true}
                    label={t('sftpUserManagement:editSftpUser.form.username.label')}
                    ariaLabel={t('sftpUserManagement:editSftpUser.form.username.label')}
                    inputId="UserNameInput"
                    value={sftpUser?.username}
                  />

                  <Input
                    disabled={true}
                    label={t('sftpUserManagement:editSftpUser.form.email.label')}
                    ariaLabel={t('sftpUserManagement:editSftpUser.form.email.label')}
                    inputId="EmailInput"
                    type="email"
                    value={email}
                    tooltipHeading={t('sftpUserManagement:editSftpUser.form.email.label')}
                    tooltipDescription={t('sftpUserManagement:editSftpUser.form.email.tooltip')}
                  />

                  <P>{t('sftpUserManagement:sftpUsers.userType.userType')}</P>
                  <Box margin="0 0 20px 0">
                    <RadioButton
                      label={t('sftpUserManagement:sftpUsers.userType.internal')}
                      selected={userType === 'internal'}
                      onClick={() => handleUserTypeChange('internal')}
                    />
                    <RadioButton
                      label={t('sftpUserManagement:sftpUsers.userType.external')}
                      selected={userType === 'external'}
                      onClick={() => handleUserTypeChange('external')}
                    />
                  </Box>

                  {['groups', 'brands', 'sites'].map(type => (
                    <SelectMulti
                      key={type}
                      ariaLabel={t(`sftpUserManagement:editSftpUser.form.${type}`)}
                      label={t(`sftpUserManagement:editSftpUser.form.${type}`)}
                      inputId={`${type}-input`}
                      onChange={getClientChangeHandler(type)}
                      options={getClientSelectOptions(type)}
                      value={getValue(type)}
                      clearable={true}
                      noOptionsPlaceholder={t('common:common.noOptionsPlaceholder')}
                    />
                  ))}
                </Form>
                <Button variation="secondary" onClick={handleResetPasswordButtonClicked}>
                  {t('sftpUserManagement:editSftpUser.form.resetPassword')}
                </Button>
              </Panel>
            </Grid>
          </SingleColumnLayout>
        </>
      </Page>
      <BottomBar>
        <SaveBar
          saveButtonLabel={t('sftpUserManagement:editSftpUser.save.saveButton')}
          onExitButtonClicked={() => (saveEnabled ? setUnsavedChangesModalIsOpen(true) : gotoSftpUserManagementPage())}
          onDiscardButtonClicked={discardChanges}
          onSaveButtonClicked={onSave}
          enableSaveButton={saveEnabled}
        />
      </BottomBar>
    </MainArea>
  );
}
